import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import TopBackNavBar from '../../components/BackNavBar';
import EwalletCard from './EwalletCard';
import { EwalletInfo } from '../../interfaces/ewallet-info';
import BodyText from '../../components/BodyText';
import { RedButton } from '../../components/Button';
import { formatCurrencyNumber } from '../../utils/amountFormat';
import { PaymentInfo } from '../../interfaces/PaymentInfo';
import bookfair from '../../interfaces/bookfair-info';
import useUpdateSessionInfo from '../../utils/updateSessionInfo';
import { Pages } from '../../App';
import { setTaxErrorData, updateEWalletInsufficientData, updateEWalletPaymentData } from '../../DumbleData';
import { ConfigContext } from '../../providers/ConfigContext';
import TextInput from '../../components/TextInput';
import PaymentButton from '../../components/PaymentButton';
import useMakeSale from '../../hooks/useMakeSale';
import { SaleResponseInfo } from '../../interfaces/sale-response-info';
import PaymentPayload from '../../interfaces/payment-payload';
import BigNumber from 'bignumber.js';

interface Props {
	pInfo: PaymentInfo;
	bfInfo: bookfair;
	setBookfairInfo: any;
	isTaxExemptChecked: boolean;
	setDefaultConfirmationEmail: any;
	setTransactionId: any;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
	setSaleResponseInfo: React.Dispatch<React.SetStateAction<SaleResponseInfo>>;
	selectedEwallet: EwalletInfo;
}

enum PaymentMethods {
	STF,
	SD,
	Cash,
}

const EwalletContent = ({ pInfo, bfInfo, setBookfairInfo, isTaxExemptChecked, setDefaultConfirmationEmail, setTransactionId, setCurrentPage, setSaleResponseInfo, selectedEwallet }: Props) => {
	const { apiBase } = useContext(ConfigContext);

	const { getSessionInfo } = useUpdateSessionInfo();
	const [status, setStatus] = useState<number>(200);
	// Following used when processing a request
	const [isButtonProcessing, setIsButtonProcessing] = useState(false);
	const [makeSale] = useMakeSale();

	const [amountApplied, setAmountApplied] = useState(true);
	const maxEwalletAmount = BigNumber.min(pInfo.amountDue, selectedEwallet.amount);
	const [ewalletAmount, setEwalletAmount] = useState<string>(maxEwalletAmount.toFixed(2));
	const ewalletAmountVal = BigNumber(ewalletAmount);
	const remainingAmount = pInfo.amountDue.minus(ewalletAmountVal);
	const stfDisabled = remainingAmount.gt(bfInfo.stfBalance);
	const sdDisabled = ewalletAmountVal.lt(pInfo.tax);
	const sdTooltipText = `Unless the sale is fully paid with Scholastic Dollars, the eWallet amount must be greater than or equal to tax. \
	Please adjust eWallet amount or select different second payment method.`;

	// Determine Second Payment Method
	const getSecondPaymentMethod = () => {
		// Share The Fair
		if (!stfDisabled) {
			return PaymentMethods.STF;
		}
		// Scholastic Dollars
		if (!sdDisabled) {
			return PaymentMethods.SD;
		}
		// Cash
		return PaymentMethods.Cash;
	};
	const [selectedButton, setSelectedButton] = useState<number>(getSecondPaymentMethod());

	const insufficientBalance = ewalletAmountVal.lt(pInfo.amountDue);
	updateEWalletInsufficientData(insufficientBalance || false);

	const submitEwalletPayment = async () => {
		setIsButtonProcessing(true);
		const payload = makePayload();
		const { data, error } = await makeSale(payload);
		if (error) {
			if (error.status === 401) {
				setCurrentPage(Pages.Landing);
			}
			setIsButtonProcessing(false);
			setStatus(error.status);
			console.error(error);
		} else if (data != null) {
			setDefaultConfirmationEmail(selectedEwallet?.ownersEmail);
			setTransactionId(data.parentTransactionId);
			setSaleResponseInfo(data);
			updateEWalletPaymentData(ewalletAmountVal.toNumber());
			window._satellite.track('ewallet-payment-payment-submit');
			setCurrentPage(Pages.Confirmation);
		}
	};

	const makePayload = () => {
		const eWalletPaymentPayload: PaymentPayload = {
			total: {
				amount: pInfo.amountDue.toNumber(),
				tax: {
					amount: pInfo.tax.toNumber(),
					rate: bfInfo.taxRate.toNumber(),
					taxableSubtotal: isTaxExemptChecked ? 0 : pInfo.itemTotal.toNumber(),
					status: bfInfo.taxStatus,
					selectedExempt: isTaxExemptChecked,
				},
			},
			tenders: {
				ewallet: {
					amount: ewalletAmountVal.toNumber(),
					id: selectedEwallet.id.toString(),
				},
			},
		};
		if (insufficientBalance) {
			// Use the second selected payment method to pay the remaining amount (selectedButton)
			switch (selectedButton) {
				case PaymentMethods.STF:
					eWalletPaymentPayload.tenders.stf = {
						amount: remainingAmount.toNumber(),
					};
					break;
				case PaymentMethods.SD:
					eWalletPaymentPayload.tenders.sd = {
						amount: remainingAmount.toNumber(),
					};
					break;
				// Cash payment
				default:
					eWalletPaymentPayload.tenders.cash = {
						amount: remainingAmount.toNumber(),
					};
					break;
			}
		}
		return eWalletPaymentPayload;
	};

	// Handle eWallet amount input change
	const handleEwalletAmountChange = (value: any) => {
		let numericValue = value.replace(/[^\d.]/g, '');
		const match = numericValue.match(/^\d*\.?\d{0,2}$/);
		if (numericValue.startsWith('.')) {
			numericValue = '0' + numericValue;
		}
		if (value === '' || match) {
			setEwalletAmount(numericValue);
		}
	};

	// When the user clicks apply, check if a proper value was entered and exit edit mode
	const handleClickApply = () => {
		if (!amountApplied && (ewalletAmountVal.lte(0) || ewalletAmountVal.gt(maxEwalletAmount) || !ewalletAmount)) {
			setEwalletAmount(maxEwalletAmount.toString());
		}
		// Auto-select the second payment method if the eWallet amount is less than the amount due
		setSelectedButton(getSecondPaymentMethod());
		setAmountApplied(!amountApplied);
	};

	const handleTaxDiscrepancy = async () => {
		const success = await getSessionInfo(apiBase, setBookfairInfo);
		if (success) {
			setCurrentPage(Pages.NewPurchase);
		} else {
			setCurrentPage(Pages.Landing);
		}
	};

	// Determine error message based on error code and eWallet balance
	let errorMessage: React.ReactNode = null;
	if (status === 409 || selectedEwallet.amount.isZero()) {
		errorMessage = 'Your eWallet balance cannot cover the amount required for payment. Please return to the previous page and choose another eWallet or payment method.';
	} else if (status === 422) {
		setTaxErrorData(true);
		errorMessage = (
			<>
				{'There appears to be a discrepancy in the sales tax amount. In order to proceed, please '}
				<StyledLink onClick={handleTaxDiscrepancy}>click here</StyledLink>
				{' to recalculate the sales tax.'}
			</>
		);
	} else if (status !== 200) {
		errorMessage = 'We are unable to process your eWallet payment. Please use a different payment method or call 877-245-0903.';
	}

	const isSubmitDisabled = !amountApplied || isButtonProcessing || status !== 200 || selectedEwallet.amount.isZero();
	const isMultiTender = amountApplied && pInfo.amountDue.gt(ewalletAmountVal) && !selectedEwallet.amount.isZero();

	return (
		<>
			<TopBackNavBar setCurrentPage={setCurrentPage} title="Input Payment Amount" location={Pages.eWallet} />
			<EwalletPage>
				<PageWrapper>
					{selectedEwallet ? (
						<StyledEwalletCard>
							<EwalletCard item={selectedEwallet} payment={false} />
						</StyledEwalletCard>
					) : null}
					<CalculationsDiv>
						<RowItemDiv>
							<ItemLabel>Item Total</ItemLabel>
							<ItemValue>{formatCurrencyNumber(pInfo.itemTotal.toNumber())}</ItemValue>
						</RowItemDiv>
						<HorizontalLine />
						<RowItemDiv>
							<ItemLabel>Tax</ItemLabel>
							<ItemValue>{formatCurrencyNumber(pInfo.tax.toNumber())}</ItemValue>
						</RowItemDiv>
						<HorizontalLine />
						<RowItemDiv>
							<ItemLabel>Amount Due</ItemLabel>
							<ItemValue>{formatCurrencyNumber(pInfo.amountDue.toNumber())}</ItemValue>
						</RowItemDiv>
						<HorizontalLine />
						{amountApplied ? (
							<RowItemDiv>
								<ItemLabel>eWallet Payment</ItemLabel>
								<ItemValue>{formatCurrencyNumber(ewalletAmountVal.toNumber())}</ItemValue>
							</RowItemDiv>
						) : (
							<StyledInputDiv>
								<TextInput label="eWallet Payment" value={ewalletAmount} inputAlign="right" onChange={handleEwalletAmountChange} />
							</StyledInputDiv>
						)}
						<StyledRightAlignDiv>
							<StyledButton onClick={handleClickApply}>{amountApplied ? 'Edit Amount' : 'Apply Amount'}</StyledButton>
						</StyledRightAlignDiv>
						{isMultiTender && (
							<>
								<HorizontalLine />
								<RowItemDiv>
									<ItemLabel>Please choose another payment to process the remaining</ItemLabel>
									<ItemValue>{formatCurrencyNumber(remainingAmount.toNumber())}</ItemValue>
								</RowItemDiv>
								<AlternativePaymentsDiv>
									<PaymentButton disabled={stfDisabled} onClick={() => setSelectedButton(PaymentMethods.STF)} isSelected={selectedButton === PaymentMethods.STF}>
										<ShareTheFairDiv>
											<InnerText>Share the Fair</InnerText>
											<FineInnerText>Balance: {formatCurrencyNumber(bfInfo.stfBalance.toNumber())}</FineInnerText>
										</ShareTheFairDiv>
									</PaymentButton>
									<PaymentButton
										disabled={sdDisabled}
										onClick={() => setSelectedButton(PaymentMethods.SD)}
										isSelected={selectedButton === PaymentMethods.SD}
										tooltipText={sdTooltipText}
										tooltipEnabled={sdDisabled}
									>
										<InnerText>Scholastic Dollars</InnerText>
									</PaymentButton>
									<PaymentButton onClick={() => setSelectedButton(PaymentMethods.Cash)} isSelected={selectedButton === PaymentMethods.Cash}>
										<InnerText>Cash</InnerText>
									</PaymentButton>
								</AlternativePaymentsDiv>
							</>
						)}
					</CalculationsDiv>
					<StyledRedButton disabled={isSubmitDisabled} handleClick={submitEwalletPayment}>
						Submit payment
					</StyledRedButton>
					{errorMessage && <ErrorText>{errorMessage}</ErrorText>}
				</PageWrapper>
			</EwalletPage>
		</>
	);
};

const EwalletPage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const StyledInputDiv = styled.div`
	width: 100%;
`;

const StyledRightAlignDiv = styled.div`
	width: 100%;
	text-align: right;
`;

const StyledButton = styled.button`
	background: none;
	border: none;
	padding: 0;
	margin: 0;
	cursor: pointer;
	color: red;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 1.5px;
	text-transform: uppercase;
	&:active {
		color: #660000;
	}
`;

const AlternativePaymentsDiv = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 20px;
	width: 100%;
	margin-top: 28px;
`;

const InnerText = styled.p`
	font-size: 16px;
	font-weight: 400;
	line-height: 19px;
	color: #e81111;
	margin: 10px 0;
	text-align: left;
`;

const FineInnerText = styled.p`
	font-size: 13px;
	font-weight: 500;
	line-height: 15px;
	color: #333333;
	margin: 0;
`;

const ShareTheFairDiv = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	gap: 6px;
	flex: 1 0 0;
	${InnerText} {
		margin: 0px;
	}
`;

const PageWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	margin-top: 38px;
	margin-bottom: 20px;
	width: 280px;
`;

const StyledEwalletCard = styled.div`
	width: 100%;
`;

const CalculationsDiv = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 12px;
	margin: 27px 0 54px 0;
`;

const RowItemDiv = styled.div`
	display: flex;
	width: 100%;
	justify-content: space-between;
	align-items: center;
`;

const ItemLabel = styled(BodyText)`
	font-size: 16px;
	font-weight: 500;
	line-height: 19px;
	text-align: left;
	margin: 0;
`;

const ItemValue = styled(BodyText)`
	font-size: 21px;
	font-weight: 500;
	line-height: 24px;
	text-align: right;
	margin: 0;
`;

const HorizontalLine = styled.div`
	width: 100%;
	height: 1px;
	background: #d7d7d7;
	margin: 0;
`;

const StyledRedButton = styled(RedButton)`
	width: 100%;
`;

const ErrorText = styled(BodyText)`
	font-size: 13px;
	font-style: normal;
	font-weight: 400;
	line-height: 15px;
	margin-top: 23px;
	text-align: left;
	color: #e81111;
`;

const StyledLink = styled.a`
	text-decoration: underline;
	cursor: pointer;
`;

export default EwalletContent;
